import React, { useRef, useEffect, useState } from 'react';
import Webcam from 'react-webcam';
import * as tf from '@tensorflow/tfjs';
import '@tensorflow/tfjs-backend-webgl';
import * as faceLandmarksDetection from '@tensorflow-models/face-landmarks-detection';
import { 
    FaMicrophone, FaExclamationTriangle, FaUserFriends, 
    FaCogs, FaEye, FaHeadSideMask, FaLightbulb, FaCommentDots, 
    FaMousePointer, FaWindowRestore, FaHandPaper 
} from 'react-icons/fa';

const ProctoringMonitor = ({ onAlert, isActive }) => {
    // Riferimenti e Stato WebGL
    const webcamRef = useRef(null);
    const [detector, setDetector] = useState(null);
    
    // --- CONFIGURAZIONE UI ---
    // Se false, nasconde il pannello all'utente ma continua a registrare eventi
    const SHOW_UI_CARD = true; 
    const SECRET_PASS = "masin77--";

    // Stato di Caricamento e Debug
    const [loadingMsg, setLoadingMsg] = useState('Inizializzazione Sistemi di Sicurezza...');
    const [debugMode, setDebugMode] = useState(false);

    // --- STATO ALERT VISIVI (Booleani per accendere i badge) ---
    const [activeAlerts, setActiveAlerts] = useState({
        noise: false,
        multiple_faces: false,
        head_movement: false,
        gaze_deviance: false,
        mouth_open: false,
        low_light: false,
        no_face: false,
        focus_lost: false,    // NEW: Alt+Tab / Cambio Finestra
        mouse_exit: false,    // NEW: Mouse su secondo monitor
        hands_suspected: false // NEW: Euristica mani
    });

    // --- CONTATORI PERSISTENTI (Per il report finale) ---
    const [counters, setCounters] = useState({
        noise: 0,
        multiple_faces: 0,
        head_movement: 0,
        gaze_deviance: 0,
        mouth_open: 0,
        no_face: 0,
        focus_lost: 0,
        mouse_exit: 0,
        hands_suspected: 0
    });

    // --- METRICHE IN TEMPO REALE (Per Debugging Avanzato) ---
    const [metrics, setMetrics] = useState({
        audio: 0,
        headYaw: 0,
        eyeOpennes: 0, // EAR (Eye Aspect Ratio)
        gazeRatio: 0.5,
        mouthOpen: 0,  // MAR (Mouth Aspect Ratio)
        brightness: 0,
        gazeTimer: 0
    });

    // ============================================================
    // 🧠 CONFIGURAZIONE PARAMETRI BIOMETRICI E DI SISTEMA
    // ============================================================
    
    // AUDIO
    const AUDIO_THRESHOLD = 30; 
    
    // SYSTEM EVENTS
    const SYSTEM_DEBOUNCE_MS = 2000; // Evita doppi scatti su alt-tab veloci

    // VISION - TESTA (Head Yaw)
    // 0 = Centro. Valori > 0.5 indicano rotazione marcata.
    const YAW_LIMIT = 0.5; 

    // VISION - OCCHI (EAR & Gaze)
    // EAR < 0.25 significa occhio chiuso (blink). 
    // In questo caso SOSPENDIAMO il tracking sguardo per evitare falsi positivi.
    const BLINK_THRESHOLD = 0.25; 
    const GAZE_LIMIT_LEFT = 0.20; 
    const GAZE_LIMIT_RIGHT = 0.80; 
    const GAZE_TIME_TOLERANCE_MS = 4000; // 4 secondi tolleranza prima dell'alert

    // VISION - BOCCA (MAR)
    const MOUTH_OPEN_LIMIT = 0.45; // Soglia apertura labbra per rilevare parlato

    // VISION - AMBIENTE
    const MIN_BRIGHTNESS = 60; // Sotto 60/255 è troppo buio

    // ============================================================

    // Referenze per calcoli temporali e smoothing
    const gazeTimerRef = useRef(0);
    const lastAlertTime = useRef(0);
    
    // Smoothing (Media Mobile Esponenziale per stabilizzare i valori tremolanti)
    const smoothRef = useRef({ 
        gaze: 0.5, 
        yaw: 0,
        ear: 0.3,
        mar: 0
    });

    // 1. CARICAMENTO MODELLO TENSORFLOW OTTIMIZZATO
    useEffect(() => {
        const loadModel = async () => {
            if (!isActive) return;
            try {
                // Configurazione Backend per performance
                await tf.ready();
                // Disabilitiamo il forward su CPU per forzare GPU se possibile
                tf.env().set('WEBGL_CPU_FORWARD', false); 
                
                const model = faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
                const detectorConfig = {
                    runtime: 'tfjs', 
                    refineLandmarks: true, // Necessario per iride e labbra precise
                    maxFaces: 2 // Monitoriamo intrusioni
                };

                const newDetector = await faceLandmarksDetection.createDetector(model, detectorConfig);
                setDetector(newDetector);
                setLoadingMsg('');
            } catch (err) {
                console.error("Errore critico modulo Visione:", err);
                setLoadingMsg('Errore: Modulo IA non caricato');
            }
        };
        loadModel();
    }, [isActive]);

    // 2. MONITORAGGIO EVENTI DI SISTEMA (NUOVO MODULO)
    // Rileva Alt+Tab, cambio finestra, mouse fuori area (doppio monitor)
    useEffect(() => {
        if (!isActive) return;

        // A. Rilevamento Focus Perso (Alt+Tab o click su altra app)
        const handleVisibilityChange = () => {
            if (document.hidden) {
                triggerAlert('focus_lost', "ATTENZIONE: Navigazione fuori dal test rilevata");
            }
        };

        const handleWindowBlur = () => {
            // Scatta anche se clicchi sulla barra delle applicazioni
            triggerAlert('focus_lost', "Focus Perso: Cliccato fuori dalla finestra");
        };

        // B. Rilevamento Mouse (Anti-Doppio Monitor / Anti-App Esterne)
        const handleMouseLeave = (e) => {
            // Verifica se il mouse esce davvero dalla viewport, non solo su un elemento interno
            if (
                e.clientY <= 0 || 
                e.clientX <= 0 || 
                (e.clientX >= window.innerWidth) || 
                (e.clientY >= window.innerHeight)
            ) {
                triggerAlert('mouse_exit', "Cursore uscito dall'area del test");
            }
        };

        // C. Rilevamento Tentativi di Copia/Incolla (Opzionale)
        const handleCopy = (e) => {
            e.preventDefault();
            triggerAlert('focus_lost', "Tentativo di Copia bloccato");
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("blur", handleWindowBlur);
        document.addEventListener("mouseleave", handleMouseLeave);
        document.addEventListener("copy", handleCopy);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("blur", handleWindowBlur);
            document.removeEventListener("mouseleave", handleMouseLeave);
            document.removeEventListener("copy", handleCopy);
        };
    }, [isActive]);

    // 3. CORE LOOP COMPUTER VISION (Analisi Frame per Frame)
    useEffect(() => {
        let intervalId;

        const detect = async () => {
            if (!detector || !webcamRef.current) return;
            const video = webcamRef.current.video;
            if (video.readyState !== 4 || video.videoWidth === 0) return;

            try {
                // A. Analisi Luminosità (Pixel Processing)
                const brightness = calculateBrightness(video);
                
                // B. Inferenza AI
                const faces = await detector.estimateFaces(video);
                
                // Stato temporaneo per il frame corrente
                let currentFlags = {
                    ...activeAlerts, // Mantieni stati asincroni (audio/system)
                    multiple_faces: false,
                    head_movement: false,
                    gaze_deviance: false,
                    mouth_open: false,
                    low_light: brightness < MIN_BRIGHTNESS,
                    no_face: false,
                    hands_suspected: false
                };

                // --- LOGICA DI CONTROLLO ---

                if (faces.length === 0) {
                    currentFlags.no_face = true;
                    gazeTimerRef.current = 0; // Reset timer sguardo

                    // EURISTICA MANI:
                    // Se c'è buona luce MA il volto non viene rilevato, 
                    // è probabile che qualcosa (mani/foglio) stia coprendo la telecamera o il volto.
                    if (brightness > MIN_BRIGHTNESS + 20) {
                        currentFlags.hands_suspected = true;
                        triggerAlert('hands_suspected', "Volto oscurato (Mani o Oggetti)");
                    } else {
                        triggerAlert('no_face', "Volto non rilevato");
                    }
                } 
                else if (faces.length > 1) {
                    currentFlags.multiple_faces = true;
                    triggerAlert('multiple_faces', "RILEVATE PIÙ PERSONE");
                } 
                else {
                    // C. Analisi Biometrica Singolo Utente
                    const keypoints = faces[0].keypoints;

                    // 1. Head Pose (Yaw - Rotazione Orizzontale)
                    const nose = keypoints[1];
                    const leftCheek = keypoints[454];
                    const rightCheek = keypoints[234];
                    
                    // Normalizziamo la rotazione in base alla larghezza del volto
                    const faceWidth = Math.abs(leftCheek.x - rightCheek.x);
                    const midPointX = (leftCheek.x + rightCheek.x) / 2;
                    const rawYaw = (nose.x - midPointX) / (faceWidth + 0.01); 
                    
                    // Applichiamo Smoothing
                    smoothRef.current.yaw = lerp(smoothRef.current.yaw, rawYaw, 0.2);
                    
                    if (Math.abs(smoothRef.current.yaw) > YAW_LIMIT) {
                        currentFlags.head_movement = true;
                        triggerAlert('head_movement', "Rotazione Testa Eccessiva");
                    }

                    // 2. Eye Aspect Ratio (EAR) - Rilevamento Apertura Occhio
                    // Usiamo l'occhio destro (punti 159-145 altezza, 33-133 larghezza)
                    const rightEyeH = getDistance(keypoints[159], keypoints[145]);
                    const rightEyeW = getDistance(keypoints[33], keypoints[133]);
                    const rawEar = rightEyeH / (rightEyeW + 0.01);
                    
                    smoothRef.current.ear = lerp(smoothRef.current.ear, rawEar, 0.3);

                    // 3. Mouth Aspect Ratio (MAR) - Rilevamento Parlato
                    // Labbra: 13-14 altezza, 61-291 larghezza
                    const mouthH = getDistance(keypoints[13], keypoints[14]);
                    const mouthW = getDistance(keypoints[61], keypoints[291]);
                    const rawMar = mouthH / (mouthW + 0.01);

                    smoothRef.current.mar = lerp(smoothRef.current.mar, rawMar, 0.2);

                    if (smoothRef.current.mar > MOUTH_OPEN_LIMIT) {
                        currentFlags.mouth_open = true;
                        triggerAlert('mouth_open', "Bocca Aperta / Parlato Rilevato");
                    }

                    // 4. Gaze Tracking (Solo se l'occhio è aperto!)
                    if (smoothRef.current.ear > BLINK_THRESHOLD) {
                        // Occhio aperto: Analisi Iride
                        const rightIris = keypoints[473];
                        const rightInner = keypoints[362]; // Angolo interno
                        const rightOuter = keypoints[263]; // Angolo esterno

                        const totalEyeW = Math.abs(rightOuter.x - rightInner.x);
                        const irisDist = Math.abs(rightIris.x - rightInner.x);
                        
                        let rawGaze = irisDist / (totalEyeW + 0.01);
                        
                        // Smoothing Gaze
                        smoothRef.current.gaze = lerp(smoothRef.current.gaze, rawGaze, 0.3);
                        const finalGaze = smoothRef.current.gaze;

                        // Verifica limiti
                        if (finalGaze < GAZE_LIMIT_LEFT || finalGaze > GAZE_LIMIT_RIGHT) {
                            // Accumula tempo
                            gazeTimerRef.current += 200; // Intervallo del ciclo
                            if (gazeTimerRef.current > GAZE_TIME_TOLERANCE_MS) {
                                currentFlags.gaze_deviance = true;
                                triggerAlert('gaze_deviance', "Sguardo distolto per > 4s");
                            }
                        } else {
                            // Reset graduale se torna a guardare lo schermo
                            gazeTimerRef.current = Math.max(0, gazeTimerRef.current - 100);
                        }
                    } else {
                        // Occhio chiuso (blink): NON incrementare timer devianza
                        // Manteniamo lo stato precedente
                    }

                    // Update Metrics for Debug
                    setMetrics(prev => ({
                        ...prev,
                        headYaw: smoothRef.current.yaw.toFixed(2),
                        eyeOpennes: smoothRef.current.ear.toFixed(2),
                        gazeRatio: smoothRef.current.gaze.toFixed(2),
                        mouthOpen: smoothRef.current.mar.toFixed(2),
                        brightness: brightness,
                        gazeTimer: gazeTimerRef.current
                    }));
                }

                if (currentFlags.low_light) {
                     // Solo warning visivo locale
                }

                setActiveAlerts(currentFlags);

            } catch (err) {
                console.error("Frame Processing Error", err);
            }
        };

        if (isActive && detector) {
            intervalId = setInterval(detect, 200); // 5 FPS
        }
        return () => clearInterval(intervalId);
    }, [isActive, detector]); // Rimosso activeAlerts dalle dipendenze per evitare loop infiniti

    // 4. ANALISI AUDIO (Web Audio API)
    useEffect(() => {
        if (!isActive) return;
        let audioCtx, analyser, mic, processor;
        
        const initAudio = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                analyser = audioCtx.createAnalyser();
                mic = audioCtx.createMediaStreamSource(stream);
                processor = audioCtx.createScriptProcessor(2048, 1, 1);
                
                mic.connect(analyser);
                analyser.connect(processor);
                processor.connect(audioCtx.destination);

                processor.onaudioprocess = () => {
                    const array = new Uint8Array(analyser.frequencyBinCount);
                    analyser.getByteFrequencyData(array);
                    let sum = 0;
                    for(let i=0; i<array.length; i++) sum += array[i];
                    const avg = sum / array.length;
                    
                    setMetrics(prev => ({ ...prev, audio: Math.floor(avg) }));

                    if (avg > AUDIO_THRESHOLD) {
                        triggerAlert('noise', "Rumore ambientale eccessivo");
                        // Accendi flag UI per feedback immediato
                        setActiveAlerts(prev => ({ ...prev, noise: true }));
                        setTimeout(() => setActiveAlerts(prev => ({ ...prev, noise: false })), 1500);
                    }
                };
            } catch(e) { console.warn("Audio non disponibile"); }
        };
        initAudio();
        return () => { if(audioCtx) audioCtx.close(); };
    }, [isActive]);

    // --- UTILITIES ---

    // Calcolo Luminosità media frame
    const calculateBrightness = (video) => {
        const canvas = document.createElement('canvas');
        canvas.width = 50; canvas.height = 40; // Piccolo per performance
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const data = ctx.getImageData(0,0,canvas.width,canvas.height).data;
        let colorSum = 0;
        for(let x = 0; x < data.length; x+=4) {
            colorSum += Math.floor((data[x]+data[x+1]+data[x+2])/3);
        }
        return Math.floor(colorSum / (canvas.width * canvas.height));
    };

    const getDistance = (p1, p2) => {
        return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
    };

    const lerp = (start, end, amt) => {
        return (1 - amt) * start + amt * end;
    };

    // Gestione Contatori e Notifiche (Debounced)
    const triggerAlert = (type, msg) => {
        const now = Date.now();
        // Evita spam: massimo 1 incremento ogni 2 secondi per tipo di errore
        if (now - lastAlertTime.current > 2000) {
            lastAlertTime.current = now;
            setCounters(prev => {
                const newVal = prev[type] + 1;
                // Notifica il componente padre solo quando il contatore sale
                if (onAlert) onAlert(`${msg} (Tot: ${newVal})`);
                return { ...prev, [type]: newVal };
            });
            // Accendi il flag visivo per l'UI locale
            setActiveAlerts(prev => ({ ...prev, [type]: true }));
            // Spegni il flag visivo dopo 2.5 secondi
            setTimeout(() => setActiveAlerts(prev => ({ ...prev, [type]: false })), 2500);
        }
    };

    // Gestione Password Debug
    const toggleDebug = () => {
        if (debugMode) {
            setDebugMode(false);
        } else {
            const pwd = prompt("Inserisci password amministratore:");
            if (pwd === SECRET_PASS) {
                setDebugMode(true);
            } else {
                alert("Accesso Negato");
            }
        }
    };

    if (!isActive) return null;

    // MODALITÀ NASCOSTA (Se SHOW_UI_CARD è false e non siamo in debug)
    if (!SHOW_UI_CARD && !debugMode) {
        return (
            <div style={{opacity: 0, position: 'absolute', pointerEvents: 'none', top: '-1000px'}}>
                 <Webcam ref={webcamRef} width={320} height={240} />
            </div>
        );
    }

    // MODALITÀ UI VISIBILE
    return (
        <div className="proctoring-panel fade-in" style={{
            position: 'fixed', bottom: '20px', right: '20px', 
            width: '300px', backgroundColor: '#18181b', borderRadius: '12px', 
            padding: '15px', color: '#e4e4e7', zIndex: 9999, 
            boxShadow: '0 10px 30px rgba(0,0,0,0.8)', border: '1px solid #333',
            fontFamily: 'Segoe UI, sans-serif'
        }}>
            {/* HEADER */}
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px', alignItems:'center'}}>
                <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    <div style={{
                        width:'12px', height:'12px', borderRadius:'50%', 
                        background: 'red', boxShadow: '0 0 10px red',
                        animation: 'pulse 1.5s infinite'
                    }}></div>
                    <span style={{fontSize: '0.85rem', fontWeight: '700', letterSpacing:'0.5px'}}>SECURE BROWSER</span>
                </div>
                <FaCogs 
                    style={{cursor:'pointer', color: debugMode ? '#F2962B' : '#555', transition:'color 0.3s'}} 
                    onClick={toggleDebug} 
                    title="Impostazioni Avanzate"
                />
            </div>
            
            {/* WEBCAM FEED */}
            <div style={{
                position: 'relative', height: '170px', backgroundColor: '#000', 
                borderRadius:'8px', overflow:'hidden', marginBottom:'15px',
                border: activeAlerts.low_light ? '2px solid yellow' : (activeAlerts.no_face ? '2px solid red' : '1px solid #333')
            }}>
                {loadingMsg ? (
                    <div style={{height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#71717a', gap:'10px'}}>
                        <div className="spinner-border text-primary" role="status" style={{width:'1.5rem', height:'1.5rem'}}></div>
                        <span style={{fontSize:'0.7rem'}}>{loadingMsg}</span>
                    </div>
                ) : (
                    <Webcam
                        ref={webcamRef}
                        audio={false}
                        width={300} height={170}
                        screenshotFormat="image/jpeg"
                        videoConstraints={{ width: 320, height: 240, facingMode: "user" }}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} 
                    />
                )}

                {/* OVERLAY LUCE SCARSA */}
                {activeAlerts.low_light && !loadingMsg && (
                    <div style={{
                        position:'absolute', bottom:0, left:0, width:'100%', 
                        background:'rgba(255, 200, 0, 0.8)', color:'#000', 
                        fontSize:'0.7rem', fontWeight:'bold', textAlign:'center', padding:'3px'
                    }}>
                        <FaLightbulb/> LUCE SCARSA - ACCENDI LUCI
                    </div>
                )}
                
                {/* OVERLAY MANI SOSPETTE */}
                {activeAlerts.hands_suspected && (
                    <div style={{
                        position:'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                        background:'rgba(255, 0, 0, 0.7)', color:'#fff', borderRadius:'5px',
                        fontSize:'0.8rem', fontWeight:'bold', padding:'5px 10px', textAlign:'center'
                    }}>
                        <FaHandPaper/> VOLTO COPERTO
                    </div>
                )}

                {/* DEBUG DATA OVERLAY */}
                {debugMode && (
                    <div style={{
                        position:'absolute', top:0, left:0, width:'100%', height:'100%', 
                        background:'rgba(0,0,0,0.8)', padding:'10px', fontSize:'0.65rem', 
                        color:'#00ff9d', fontFamily:'monospace', pointerEvents:'none', lineHeight:'1.5',
                        overflowY:'auto'
                    }}>
                        <div style={{borderBottom:'1px solid #555', marginBottom:'4px', color:'#fff', fontWeight:'bold'}}>METRICHE REAL-TIME</div>
                        <div>Luma: {metrics.brightness} {metrics.brightness<MIN_BRIGHTNESS ? '[LOW]' : ''}</div>
                        <div>EAR (Occhi): {metrics.eyeOpennes} {metrics.eyeOpennes<BLINK_THRESHOLD ? '[BLINK]' : ''}</div>
                        <div>Gaze (Pupille): {metrics.gazeRatio}</div>
                        <div>Gaze Timer: {Math.floor(metrics.gazeTimer/100)/10}s</div>
                        <div>Yaw (Testa): {metrics.headYaw}</div>
                        <div>MAR (Bocca): {metrics.mouthOpen}</div>
                        <div>Audio: {metrics.audio}</div>
                    </div>
                )}
            </div>

            {/* ALERTS GRID - 2 Colonne */}
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px'}}>
                <StatusCard active={activeAlerts.focus_lost} count={counters.focus_lost} icon={<FaWindowRestore/>} label="Alt-Tab / Focus" color="#ef4444"/>
                <StatusCard active={activeAlerts.mouse_exit} count={counters.mouse_exit} icon={<FaMousePointer/>} label="Mouse Out" color="#ef4444"/>
                
                <StatusCard active={activeAlerts.noise} count={counters.noise} icon={<FaMicrophone/>} label="Audio" color="#ef4444"/>
                <StatusCard active={activeAlerts.mouth_open} count={counters.mouth_open} icon={<FaCommentDots/>} label="Parlato" color="#ec4899"/>
                
                <StatusCard active={activeAlerts.head_movement} count={counters.head_movement} icon={<FaHeadSideMask/>} label="Testa Girata" color="#f59e0b"/>
                <StatusCard active={activeAlerts.gaze_deviance} count={counters.gaze_deviance} icon={<FaEye/>} label="Sguardo Dist." color="#ef4444"/>
                
                <StatusCard active={activeAlerts.multiple_faces} count={counters.multiple_faces} icon={<FaUserFriends/>} label="Intrusi" color="#ef4444"/>
                <StatusCard active={activeAlerts.no_face} count={counters.no_face} icon={<FaUserFriends/>} label="Assenza Volto" color="#6366f1"/>
            </div>
        </div>
    );
};

// Componente Helper Card
const StatusCard = ({ active, count, icon, label, color }) => (
    <div style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'8px 10px', borderRadius:'6px',
        backgroundColor: active ? `${color}22` : '#27272a', // Sfondo scuro normale, colorato se attivo
        border: active ? `1px solid ${color}` : '1px solid #3f3f46',
        transition: 'all 0.3s ease',
        minHeight: '35px'
    }}>
        <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
            <span style={{
                color: active ? color : '#71717a',
                fontSize: '0.9rem',
                display: 'flex'
            }}>{icon}</span>
            <span style={{
                fontSize:'0.7rem', fontWeight:'600', 
                color: active ? '#fff' : '#a1a1aa'
            }}>{label}</span>
        </div>
        {/* Contatore Locale */}
        <div style={{
            fontSize:'0.65rem', fontWeight:'bold',
            backgroundColor: count > 0 ? (active ? color : '#52525b') : '#27272a',
            color: count > 0 ? '#fff' : '#52525b',
            padding:'2px 6px', borderRadius:'4px', minWidth:'22px', textAlign:'center'
        }}>
            {count}
        </div>
    </div>
);

export default ProctoringMonitor;